/**
 * AuditLogPage — reviews audit events and cost-basis drift alerts.
 *
 * Admins see every event across the platform. Non-admins see only their own
 * actions (backend enforces the split; this page just shows what's returned).
 *
 * Backed by GET /api/audit which reads from an in-memory ring buffer of the
 * last 1000 events plus a Postgres backing table.
 *
 * The "Cost-Basis Drift" panel (tab) mines the same audit log for cumulative
 * avg_cost / value drift across many edits — a holding whose cost basis has
 * silently shifted across many small edits, corrupting CGT / IHT.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  ScrollText, RefreshCw, Filter, LogIn, LogOut,
  UserCheck, KeyRound, UserPlus, Shield, Zap,
  TrendingDown, AlertTriangle, CheckCircle2, ShieldCheck,
  XCircle, MinusCircle,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import { getCostBasisDrift, getDataIntegrityScore } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import type {
  CostBasisDriftAlert, CostBasisDriftResponse,
  DataIntegrityScoreResponse, DataIntegrityComponent,
} from '@/lib/types'

interface AuditEntry {
  id: number
  timestamp: string
  user: string
  action: string
  details: Record<string, any>
}

const ACTION_ICONS: Record<string, { icon: any; color: string }> = {
  login:                    { icon: LogIn,     color: 'text-emerald' },
  logout:                   { icon: LogOut,    color: 'text-text-muted' },
  sso_login:                { icon: LogIn,     color: 'text-blue-light' },
  waitlist_signup:          { icon: UserPlus,  color: 'text-amber' },
  waitlist_approved:        { icon: UserCheck, color: 'text-emerald' },
  user_registered:          { icon: UserPlus,  color: 'text-emerald' },
  password_changed:         { icon: KeyRound,  color: 'text-blue-light' },
  change_password_failed:   { icon: KeyRound,  color: 'text-rose-light' },
  household_share_code_issued: { icon: Shield, color: 'text-blue-light' },
  household_joined:         { icon: UserCheck, color: 'text-emerald' },
  trade_approved:           { icon: Zap,       color: 'text-emerald' },
  trade_rejected:           { icon: Zap,       color: 'text-rose-light' },
  agent_ran:                { icon: Zap,       color: 'text-blue-light' },
}

function ActionIcon({ action }: { action: string }) {
  const config = ACTION_ICONS[action] ?? { icon: ScrollText, color: 'text-text-muted' }
  const Icon = config.icon
  return <Icon size={14} className={`${config.color} shrink-0`} aria-hidden />
}

function formatTimestamp(ts: string): string {
  try {
    return new Date(ts).toLocaleString('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'medium',
    })
  } catch {
    return ts
  }
}

function summariseDetails(details: Record<string, any>): string {
  if (!details || Object.keys(details).length === 0) return ''
  const parts: string[] = []
  for (const [k, v] of Object.entries(details)) {
    if (v == null) continue
    const pretty = typeof v === 'object' ? JSON.stringify(v) : String(v)
    parts.push(`${k}=${pretty}`)
  }
  return parts.join(' · ')
}

// ── Drift level badge ─────────────────────────────────────────────────────────

function DriftBadge({ level }: { level: CostBasisDriftAlert['level'] }) {
  const config = {
    high: { label: 'HIGH', cls: 'bg-rose/15 text-rose-light border border-rose/30', icon: AlertTriangle },
    medium: { label: 'MEDIUM', cls: 'bg-amber/15 text-amber border border-amber/30', icon: TrendingDown },
    low: { label: 'LOW', cls: 'bg-blue-light/10 text-blue-light border border-blue-light/20', icon: CheckCircle2 },
  }[level] ?? { label: level.toUpperCase(), cls: 'bg-surface-hover text-text-muted border border-border', icon: ScrollText }

  const Icon = config.icon
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${config.cls}`}>
      <Icon size={10} aria-hidden />
      {config.label}
    </span>
  )
}

function formatPct(v: number | null | undefined): string {
  if (v == null || !isFinite(v)) return 'n/a'
  const sign = v >= 0 ? '+' : ''
  return `${sign}${v.toFixed(1)}%`
}

function formatTs(ts: string | null | undefined): string {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleDateString('en-GB', { dateStyle: 'medium' })
  } catch {
    return ts
  }
}

// ── Cost-Basis Drift Panel ────────────────────────────────────────────────────

function CostBasisDriftPanel() {
  const [data, setData] = useState<CostBasisDriftResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchDrift = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getCostBasisDrift()
      setData(result)
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? err?.message ?? 'Failed to load drift alerts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDrift()
  }, [])

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="panel-title flex items-center gap-2">
            <TrendingDown size={15} className="text-amber" aria-hidden />
            Cost-Basis Drift Alerts
          </h2>
          <p className="text-xs text-text-secondary mt-0.5">
            Cumulative avg_cost / value drift mined from your holding-edit audit trail.
            Material drift corrupts CGT and IHT calculations.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchDrift}
          className="btn-secondary text-xs gap-1.5"
          disabled={loading}
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      {/* Summary badges */}
      {data && (
        <div className="flex flex-wrap gap-3 text-xs">
          {data.high_count > 0 && (
            <span className="flex items-center gap-1 text-rose-light font-semibold">
              <AlertTriangle size={12} aria-hidden />
              {data.high_count} high
            </span>
          )}
          {data.medium_count > 0 && (
            <span className="flex items-center gap-1 text-amber font-semibold">
              <TrendingDown size={12} aria-hidden />
              {data.medium_count} medium
            </span>
          )}
          {data.low_count > 0 && (
            <span className="flex items-center gap-1 text-blue-light">
              {data.low_count} low
            </span>
          )}
          <span className="text-text-muted ml-auto">{data.note}</span>
        </div>
      )}

      {error && (
        <div className="card p-3 border-rose/30 bg-rose/5 text-sm text-rose-light">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="space-y-2">
          {[0, 1, 2].map(i => (
            <div key={i} className="h-12 skeleton" />
          ))}
        </div>
      )}

      {!loading && data && data.alerts.length === 0 && (
        <div className="card p-8 text-center">
          <CheckCircle2 size={28} className="text-emerald mx-auto mb-3" aria-hidden />
          <p className="text-sm text-text-primary font-medium">No material cost-basis drift detected</p>
          <p className="text-xs text-text-muted mt-1">{data.note}</p>
        </div>
      )}

      {data && data.alerts.length > 0 && (
        <div className="card overflow-hidden">
          {/* Mobile: card stack. Desktop: table. */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="table-pro w-full text-xs">
              <thead className="bg-surface-DEFAULT border-b border-border">
                <tr>
                  <th className="text-left py-2 px-3 text-text-muted uppercase tracking-wider text-[10px]">Symbol</th>
                  <th className="text-left py-2 px-3 text-text-muted uppercase tracking-wider text-[10px]">Field</th>
                  <th className="!text-right py-2 px-3 text-text-muted uppercase tracking-wider text-[10px]">Net drift</th>
                  <th className="!text-right py-2 px-3 text-text-muted uppercase tracking-wider text-[10px]">Edits</th>
                  <th className="!text-right py-2 px-3 text-text-muted uppercase tracking-wider text-[10px]">Churn</th>
                  <th className="text-left py-2 px-3 text-text-muted uppercase tracking-wider text-[10px]">Level</th>
                  <th className="text-left py-2 px-3 text-text-muted uppercase tracking-wider text-[10px] w-64">Reason</th>
                  <th className="text-left py-2 px-3 text-text-muted uppercase tracking-wider text-[10px]">First / Last</th>
                </tr>
              </thead>
              <tbody>
                {data.alerts.map((alert, i) => (
                  <tr
                    key={`${alert.holding_id ?? alert.symbol}-${i}`}
                    className={clsx(
                      'border-b border-border/50 last:border-0 hover:bg-surface-hover/50',
                      alert.level === 'high' && 'bg-rose/5',
                      alert.level === 'medium' && 'bg-amber/5',
                    )}
                  >
                    <td className="py-2 px-3 font-mono font-semibold text-text-primary">
                      {alert.symbol ?? '—'}
                    </td>
                    <td className="py-2 px-3 text-text-muted">{alert.field}</td>
                    <td className={clsx(
                      'py-2 px-3 text-right font-mono font-semibold',
                      (alert.net_change_pct ?? 0) < 0 ? 'text-rose-light' : 'text-emerald',
                    )}>
                      {formatPct(alert.net_change_pct)}
                    </td>
                    <td className="py-2 px-3 text-right text-text-secondary">{alert.edit_count}</td>
                    <td className="py-2 px-3 text-right text-text-muted">{formatPct(alert.total_abs_drift_pct)}</td>
                    <td className="py-2 px-3">
                      <DriftBadge level={alert.level} />
                    </td>
                    <td className="py-2 px-3 text-text-muted text-[11px] max-w-[260px] break-words">
                      {alert.reason}
                    </td>
                    <td className="py-2 px-3 text-text-muted font-mono text-[10px] whitespace-nowrap">
                      {formatTs(alert.first_seen)} → {formatTs(alert.last_seen)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card stack */}
          <div className="sm:hidden divide-y divide-border">
            {data.alerts.map((alert, i) => (
              <div
                key={`m-${alert.holding_id ?? alert.symbol}-${i}`}
                className={clsx(
                  'p-3 space-y-1',
                  alert.level === 'high' && 'bg-rose/5',
                  alert.level === 'medium' && 'bg-amber/5',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-semibold text-text-primary text-sm">
                    {alert.symbol ?? '—'}
                  </span>
                  <DriftBadge level={alert.level} />
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-text-muted">
                  <span>Field: <span className="text-text-secondary">{alert.field}</span></span>
                  <span>Net: <span className={clsx('font-mono font-semibold', (alert.net_change_pct ?? 0) < 0 ? 'text-rose-light' : 'text-emerald')}>{formatPct(alert.net_change_pct)}</span></span>
                  <span>Edits: <span className="text-text-secondary">{alert.edit_count}</span></span>
                  <span>Churn: <span className="text-text-secondary">{formatPct(alert.total_abs_drift_pct)}</span></span>
                </div>
                <p className="text-[11px] text-text-muted leading-snug">{alert.reason}</p>
                <p className="text-[10px] text-text-muted font-mono">
                  {formatTs(alert.first_seen)} → {formatTs(alert.last_seen)}
                </p>
              </div>
            ))}
          </div>

          <div className="px-3 py-2 border-t border-border text-[10px] text-text-muted">
            {data.alerts.length} alert{data.alerts.length !== 1 ? 's' : ''} from{' '}
            {data.total_holdings_with_history} holding{data.total_holdings_with_history !== 1 ? 's' : ''} with audit history
          </div>
        </div>
      )}
    </div>
  )
}

// ── Data Integrity Score Panel ────────────────────────────────────────────────

function StatusIcon({ status }: { status: DataIntegrityComponent['status'] }) {
  if (status === 'good') return <CheckCircle2 size={14} className="text-emerald shrink-0" aria-hidden />
  if (status === 'warn') return <MinusCircle  size={14} className="text-amber shrink-0"   aria-hidden />
  return                        <XCircle      size={14} className="text-rose-light shrink-0" aria-hidden />
}

function GradeBadge({ grade, score }: { grade: string; score: number }) {
  const cls: Record<string, string> = {
    A: 'bg-emerald/15 text-emerald border-emerald/30',
    B: 'bg-blue-light/10 text-blue-light border-blue-light/20',
    C: 'bg-amber/15 text-amber border-amber/30',
    D: 'bg-orange-400/15 text-orange-400 border-orange-400/30',
    F: 'bg-rose/15 text-rose-light border-rose/30',
  }
  return (
    <div className={`flex flex-col items-center justify-center w-20 h-20 rounded-xl border-2 ${cls[grade] ?? cls['F']}`}>
      <span className="text-3xl font-bold leading-none">{grade}</span>
      <span className="text-xs font-semibold mt-0.5 opacity-80">{score}/100</span>
    </div>
  )
}

function DataIntegrityScorePanel() {
  const [data, setData] = useState<DataIntegrityScoreResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchScore = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getDataIntegrityScore()
      setData(result)
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? err?.message ?? 'Failed to load integrity score')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchScore() }, [])

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="panel-title flex items-center gap-2">
            <ShieldCheck size={15} className="text-blue-light" aria-hidden />
            Holdings Data Integrity Score
          </h2>
          <p className="text-xs text-text-secondary mt-0.5">
            Rolls up cost-basis drift, recent edit flags and price staleness into one headline grade.
            Honest heuristic — see the note below for coverage caveats.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchScore}
          className="btn-secondary text-xs gap-1.5"
          disabled={loading}
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      {error && (
        <div className="card p-3 border-rose/30 bg-rose/5 text-sm text-rose-light">{error}</div>
      )}

      {loading && !data && (
        <div className="space-y-2">
          {[0, 1, 2].map(i => (
            <div key={i} className="h-14 skeleton" />
          ))}
        </div>
      )}

      {data && (
        <>
          {/* Score headline */}
          <div className="card p-4 flex items-center gap-5 flex-wrap">
            <GradeBadge grade={data.grade} score={data.score} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text-primary">{data.summary}</p>
              <p className="text-xs text-text-muted mt-1 italic">{data.note}</p>
            </div>
          </div>

          {/* Component breakdown */}
          <div className="card overflow-hidden">
            <div className="px-3 py-2 border-b border-border text-[10px] uppercase tracking-wider text-text-muted font-semibold">
              Component Breakdown
            </div>
            <div className="divide-y divide-border/60">
              {(data.components ?? []).map(c => (
                <div key={c.name} className="flex items-start gap-3 p-3">
                  <StatusIcon status={c.status} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-text-primary">{c.name}</span>
                      <span className={clsx(
                        'text-[10px] font-mono',
                        c.penalty === 0 ? 'text-text-muted' : 'text-rose-light font-semibold',
                      )}>
                        {c.penalty === 0 ? 'no penalty' : `-${c.penalty} pts`}
                      </span>
                    </div>
                    <p className="text-[11px] text-text-muted mt-0.5 leading-snug">{c.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Raw counts */}
          <div className="card p-3">
            <p className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-2">
              Raw Signal Counts
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-text-secondary">
              <span>Holdings: <span className="font-semibold text-text-primary">{data.raw_counts?.total_holdings ?? '—'}</span></span>
              <span>High drift: <span className={clsx('font-semibold', (data.raw_counts?.high_drift ?? 0) > 0 ? 'text-rose-light' : 'text-text-primary')}>{data.raw_counts?.high_drift ?? 0}</span></span>
              <span>Medium drift: <span className={clsx('font-semibold', (data.raw_counts?.medium_drift ?? 0) > 0 ? 'text-amber' : 'text-text-primary')}>{data.raw_counts?.medium_drift ?? 0}</span></span>
              <span>Recent flags: <span className={clsx('font-semibold', (data.raw_counts?.recent_high_flags ?? 0) > 0 ? 'text-rose-light' : 'text-text-primary')}>{data.raw_counts?.recent_high_flags ?? 0}</span></span>
              <span>Stale 48h–7d: <span className={clsx('font-semibold', (data.raw_counts?.stale_48h ?? 0) > 0 ? 'text-amber' : 'text-text-primary')}>{data.raw_counts?.stale_48h ?? 0}</span></span>
              <span>Stale &gt;7d: <span className={clsx('font-semibold', (data.raw_counts?.stale_7d ?? 0) > 0 ? 'text-rose-light' : 'text-text-primary')}>{data.raw_counts?.stale_7d ?? 0}</span></span>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

export default function AuditLogPage() {
  const user = useAuth(s => s.user)
  const isAdmin = user?.role === 'admin'
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterAction, setFilterAction] = useState<string>('')
  const [filterUser, setFilterUser] = useState<string>('')
  const [limit, setLimit] = useState(100)
  const [tab, setTab] = useState<'log' | 'drift' | 'integrity'>('log')

  const fetchAudit = async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, any> = { limit }
      if (filterAction) params.action = filterAction
      if (filterUser) params.user = filterUser
      const { data } = await api.get('/api/audit', { params })
      setEntries(Array.isArray(data) ? data : [])
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load audit log')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAudit()
  }, [limit, filterAction, filterUser]) // eslint-disable-line

  const { actionOptions, userOptions, actionCounts } = useMemo(() => {
    const actions = new Set<string>()
    const users = new Set<string>()
    const counts: Record<string, number> = {}
    for (const e of entries) {
      actions.add(e.action)
      if (e.user) users.add(e.user)
      counts[e.action] = (counts[e.action] ?? 0) + 1
    }
    return {
      actionOptions: Array.from(actions).sort(),
      userOptions: Array.from(users).sort(),
      actionCounts: counts,
    }
  }, [entries])

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <ScrollText size={20} className="text-blue-light" aria-hidden />
            Audit Log
          </h1>
          <p className="text-sm text-text-secondary">
            {isAdmin
              ? 'Platform-wide activity — every login, approval, and change.'
              : 'Your own activity on the platform.'}
          </p>
        </div>
        {tab === 'log' && (
          <button
            type="button"
            onClick={fetchAudit}
            className="btn-secondary text-xs gap-1.5"
            disabled={loading}
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
            Refresh
          </button>
        )}

      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        <button
          type="button"
          onClick={() => setTab('log')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            tab === 'log'
              ? 'border-blue-light text-blue-light'
              : 'border-transparent text-text-muted hover:text-text-primary',
          )}
        >
          Event Log
        </button>
        <button
          type="button"
          onClick={() => setTab('drift')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5',
            tab === 'drift'
              ? 'border-amber text-amber'
              : 'border-transparent text-text-muted hover:text-text-primary',
          )}
        >
          <TrendingDown size={13} aria-hidden />
          Cost-Basis Drift
        </button>
        <button
          type="button"
          onClick={() => setTab('integrity')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5',
            tab === 'integrity'
              ? 'border-blue-light text-blue-light'
              : 'border-transparent text-text-muted hover:text-text-primary',
          )}
        >
          <ShieldCheck size={13} aria-hidden />
          Data Integrity
        </button>
      </div>

      {/* Drift panel */}
      {tab === 'drift' && <CostBasisDriftPanel />}

      {/* Data Integrity Score panel */}
      {tab === 'integrity' && <DataIntegrityScorePanel />}

      {tab === 'log' && (<>
      {/* Filters */}
      <div className="glass rounded-xl p-4 flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[140px]">
          <label htmlFor="audit-action-filter" className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">
            <Filter size={10} className="inline mr-1" aria-hidden />
            Action
          </label>
          <select
            id="audit-action-filter"
            value={filterAction}
            onChange={e => setFilterAction(e.target.value)}
            className="input text-xs"
          >
            <option value="">All actions</option>
            {actionOptions.map(a => (
              <option key={a} value={a}>
                {a} ({actionCounts[a] ?? 0})
              </option>
            ))}
          </select>
        </div>
        {isAdmin && (
          <div className="flex-1 min-w-[140px]">
            <label htmlFor="audit-user-filter" className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">
              User
            </label>
            <select
              id="audit-user-filter"
              value={filterUser}
              onChange={e => setFilterUser(e.target.value)}
              className="input text-xs"
            >
              <option value="">All users</option>
              {userOptions.map(u => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
        )}
        <div className="min-w-[120px]">
          <label htmlFor="audit-limit" className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">
            Limit
          </label>
          <select
            id="audit-limit"
            value={limit}
            onChange={e => setLimit(Number(e.target.value))}
            className="input text-xs"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={250}>250</option>
            <option value={500}>500</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => setFilterAction('holding_*')}
          className={`btn-ghost text-xs ${filterAction === 'holding_*' ? 'text-blue' : 'text-text-muted'}`}
          title="Show only holding_added / holding_updated / holding_deleted"
        >
          Holdings only
        </button>
        {(filterAction || filterUser) && (
          <button
            type="button"
            onClick={() => {
              setFilterAction('')
              setFilterUser('')
            }}
            className="btn-ghost text-xs text-text-muted"
          >
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <div className="card p-4 border-rose/30 bg-rose/5 text-sm text-rose-light">
          {error}
        </div>
      )}

      {/* Entries */}
      {loading && entries.length === 0 ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="h-12 skeleton" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <div className="card p-10 text-center">
          <ScrollText size={32} className="text-text-muted mx-auto mb-3" />
          <p className="text-sm text-text-primary font-medium">No audit entries</p>
          <p className="text-xs text-text-muted mt-1">
            {filterAction || filterUser
              ? 'Try clearing your filters.'
              : 'Nothing has been logged yet.'}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="max-h-[70vh] overflow-y-auto">
            <table className="table-pro w-full text-xs">
              <thead className="sticky top-0 bg-surface-DEFAULT border-b border-border z-10">
                <tr>
                  <th className="text-left py-2 px-3 text-text-muted uppercase tracking-wider text-[10px] w-40">
                    Time
                  </th>
                  <th className="text-left py-2 px-3 text-text-muted uppercase tracking-wider text-[10px] w-20">
                    User
                  </th>
                  <th className="text-left py-2 px-3 text-text-muted uppercase tracking-wider text-[10px] w-52">
                    Action
                  </th>
                  <th className="text-left py-2 px-3 text-text-muted uppercase tracking-wider text-[10px]">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <tr
                    key={entry.id}
                    className={clsx(
                      'border-b border-border/50 last:border-0 hover:bg-surface-hover/50',
                      entry.action === 'change_password_failed' && 'bg-rose/5',
                    )}
                  >
                    <td className="py-2 px-3 text-text-muted font-mono text-[11px]">
                      {formatTimestamp(entry.timestamp)}
                    </td>
                    <td className="py-2 px-3">
                      <span className="font-mono text-[11px] font-semibold text-text-secondary">
                        {entry.user}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1.5">
                        <ActionIcon action={entry.action} />
                        <span className="text-text-primary">{entry.action}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-text-muted font-mono text-[10px] break-all">
                      {summariseDetails(entry.details)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 border-t border-border text-[10px] text-text-muted flex items-center justify-between">
            <span>
              Showing {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
              {isAdmin ? '' : ' (scoped to your account)'}
            </span>
            <span>Ring buffer: last 1000 events + Postgres archive</span>
          </div>
        </div>
      )}
      </>)}
    </div>
  )
}
